import * as React from 'react';
import { COACH_MARKETPLACE_PAGE, SIGNUP_PAGE } from 'constants/pages';
import { GenderEnum } from 'types/generated/client';
import {
  PRIMARY_TENNIS_SYSTEM_IDS,
  TENNIS_SYSTEMS,
  TennisRatingSystem,
  convertTennisRating,
  tennisRatingToSkillLevel,
  tennisSkillLevelTierInfo,
} from 'utils/shared/tennisRatingConverter';
import { trackEvent } from 'services/client/analytics';
import {
  TOOL_RATING_CONVERTER_FIND_COACH_CLICKED,
  TOOL_RATING_CONVERTER_SIGNUP_CLICKED,
} from 'services/client/analytics/events';
import SafeAreaPage from 'layouts/SafeAreaPage';
import Head from 'components/utilities/Head';
import Faqs from 'components/Faqs';
import Footer from 'components/Footer';
import { ButtonLink } from 'components/Button';
import Converter from './Converter';
import ComparisonTable from './ComparisonTable';
import ScoutingReportWidget from './ScoutingReportWidget';
import { FAQS } from './faqs';

export interface PageProps {
  jsonLd?: string;
}

export default function TennisRatingConverter({ jsonLd }: PageProps) {
  const [fromSystem, setFromSystem] = React.useState<TennisRatingSystem>('utr');
  const [value, setValue] = React.useState<number>(6.0);
  const [gender, setGender] = React.useState<GenderEnum | null>(GenderEnum.Male);

  const handleResultChange = React.useCallback(
    (nextSystem: TennisRatingSystem, nextValue: number, nextGender: GenderEnum | null) => {
      setFromSystem(nextSystem);
      setValue(nextValue);
      setGender(nextGender);
    },
    [],
  );

  const skillLevel = tennisRatingToSkillLevel(value, fromSystem, { gender });
  const tier = tennisSkillLevelTierInfo(skillLevel);
  const conversions = convertTennisRating(value, fromSystem, { gender });
  const utrRating = conversions.find((c) => c.system === 'utr')?.value ?? value;
  const coachSearchUrl = `${COACH_MARKETPLACE_PAGE}?skillLevel=${encodeURIComponent(
    skillLevel,
  )}`;

  const handleFindCoachClick = () => {
    trackEvent(TOOL_RATING_CONVERTER_FIND_COACH_CLICKED, {
      sport: 'tennis',
      skill_level: skillLevel,
      from_system: fromSystem,
      from_value: value,
      gender,
    });
  };

  const handleSignupClick = () => {
    trackEvent(TOOL_RATING_CONVERTER_SIGNUP_CLICKED, {
      sport: 'tennis',
      from_system: fromSystem,
      from_value: value,
      gender,
    });
  };

  return (
    <>
      <Head
        title="Tennis Rating Converter: UTR, NTRP, WTN, ITN"
        description="Convert your UTR, NTRP, WTN, ITN, Dynamic NTRP, or self-rated tennis rating instantly. See equivalents across every major system with confidence ranges."
        canonicalType="currentUrlStripQuery"
      />
      {!!jsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
      )}
      <SafeAreaPage isHideSidebar isShowTopNav>
        <main className="mx-auto w-full max-w-5xl px-6 py-12">
          <header className="mb-10 text-center">
            <h1 className="typography-product-display mb-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              Tennis Rating Converter: UTR, NTRP, WTN
            </h1>
            <p className="tool-tldr typography-product-body text-lg mx-auto max-w-3xl text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              Convert between every major tennis rating system instantly. Enter your rating once
              (UTR, NTRP, Dynamic NTRP, WTN, ITN, or self-rated) and see the equivalents in all
              the others. You’ll get confidence ranges and a plain-English explanation of what
              each level looks like on the court.
            </p>
          </header>

          <section className="mb-12" aria-labelledby="converter-heading">
            <h2 id="converter-heading" className="sr-only">
              Converter
            </h2>
            <Converter onResultChange={handleResultChange} />
          </section>

          <section className="mb-16">
            <ScoutingReportWidget
              utrRating={utrRating}
              skillLevel={skillLevel}
              onFindCoachClick={handleFindCoachClick}
              onSignupClick={handleSignupClick}
            />
          </section>

          <section className="mb-16" aria-labelledby="comparison-heading">
            <h2
              id="comparison-heading"
              className="typography-product-heading mb-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
            >
              Tennis rating comparison table
            </h2>
            <p className="typography-product-body mb-6 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              The canonical reference table is anchored on UTR (the global standard) and aligned
              across NTRP, WTN, and ITN. Values are approximate since there is no official
              cross-system conversion published by any rating organization, and NTRP → UTR
              accuracy depends on division (men’s vs. women’s).
            </p>
            <ComparisonTable />
          </section>

          <section className="mb-16" aria-labelledby="differences-heading">
            <h2
              id="differences-heading"
              className="typography-product-heading mb-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
            >
              What’s the difference between UTR, NTRP, WTN, ITN, and self-rated?
            </h2>
            <p className="typography-product-body mb-4 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              Each of these systems measures something slightly different, which is why a player
              can carry several tennis ratings at once and see them disagree.
            </p>
            <ul className="divide-y divide-color-divider-lightmode-primary border-y border-color-divider-lightmode-primary dark:divide-color-divider-darkmode-primary dark:border-color-divider-darkmode-primary">
              {PRIMARY_TENNIS_SYSTEM_IDS.map((id) => {
                const def = TENNIS_SYSTEMS[id];
                return (
                  <li key={id} className="py-5">
                    <h3 className="typography-product-subheading mb-1 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                      {def.shortName} ({def.longName})
                    </h3>
                    <p className="typography-product-body text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                      {def.oneLineDescription}
                    </p>
                    <p className="typography-product-body text-sm mt-2 text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                      Scale: {def.scaleMin.toFixed(1)}–{def.scaleMax.toFixed(1)}
                      {def.isInverted ? ' (lower = stronger)' : ''} ·{' '}
                      <a
                        href={def.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-color-bg-lightmode-brand hover:underline"
                      >
                        {def.websiteLabel}
                      </a>
                    </p>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="mb-16" aria-labelledby="itn-heading">
            <h2
              id="itn-heading"
              className="typography-product-heading mb-3 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
            >
              What happened to ITN?
            </h2>
            <p className="typography-product-body text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              ITN (ITF Tennis Number) was the ITF’s previous global rating, running from 10.3
              (beginner) to 1 (world-class) in 0.5 steps. It was effectively replaced in 2022 by
              the World Tennis Number (WTN), which is finer-grained, federation-fed, and
              algorithmic. Players with old ITN numbers can use them as a starting point in this
              converter, but new ITF-aligned ratings now publish as WTN.
            </p>
          </section>

          <section className="mb-16" aria-labelledby="accuracy-heading">
            <h2
              id="accuracy-heading"
              className="typography-product-heading mb-3 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
            >
              How accurate are these conversions?
            </h2>
            <p className="typography-product-body text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              Conversions between systems are approximations because each system measures
              overlapping but distinct things. UTR ↔ Dynamic NTRP is the tightest pairing at the
              upper levels because both are algorithmic. UTR ↔ NTRP shifts by division — women’s
              NTRP maps about half a level lower in UTR than men’s — which is why this converter
              applies a gender adjustment. UTR ↔ WTN is solid because WTN normalizes globally
              the same way UTR does, though the scales are inverted. UTR ↔ self-rated has the
              widest band because self-rating is an honor system. Treat any cross-system value
              as an estimate with a confidence range.
            </p>
          </section>

          <section className="mb-16" aria-labelledby="where-heading">
            <h2
              id="where-heading"
              className="typography-product-heading mb-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
            >
              Where to find your rating
            </h2>
            <ul className="space-y-3">
              {PRIMARY_TENNIS_SYSTEM_IDS.map((id) => {
                const def = TENNIS_SYSTEMS[id];
                return (
                  <li
                    key={id}
                    className="typography-product-body text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"
                  >
                    <strong className="text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                      {def.shortName}:
                    </strong>{' '}
                    {def.whereToFind}
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="mb-16" aria-labelledby="faq-heading">
            <h2 id="faq-heading" className="sr-only">
              Frequently asked questions
            </h2>
            <Faqs questionsAnswers={FAQS} />
          </section>

          <section
            className="mb-16 rounded-3xl bg-color-bg-lightmode-brand px-6 py-12 text-center text-white md:px-12 md:py-16"
            aria-labelledby="bottom-cta-heading"
          >
            <h2
              id="bottom-cta-heading"
              className="typography-product-heading mb-3 text-white"
            >
              Ready to play at your level?
            </h2>
            <p className="typography-product-body mx-auto mb-8 max-w-xl text-white/90">
              Now you know where you stand across {tier.label.toLowerCase()} territory. Find a
              tennis coach who specializes at your level, or save your rating to your Bounce
              profile so you can track progress over time.
            </p>
            <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
              <ButtonLink
                href={coachSearchUrl}
                variant="inverted"
                size="lg"
                isInline
                onClick={handleFindCoachClick}
              >
                Find a tennis coach
              </ButtonLink>
              <ButtonLink
                href={SIGNUP_PAGE}
                variant="primary"
                size="lg"
                isInline
                onClick={handleSignupClick}
              >
                Save my rating
              </ButtonLink>
            </div>
          </section>
        </main>
        <Footer />
      </SafeAreaPage>
    </>
  );
}
